/**
 * Version information utilities
 * Fetches and displays app version info for debugging and cache tracking
 */

let versionInfo = null;

/**
 * Fetch version information from version.json
 * @returns {Promise<Object>} Version information object
 */
export async function fetchVersionInfo() {
	if (versionInfo) {
		return versionInfo;
	}

	try {
		const response = await fetch('/version.json?t=' + Date.now());
		versionInfo = await response.json();
		return versionInfo;
	} catch (error) {
		console.warn('Could not fetch version info:', error);
		return {
			version: 'unknown',
			buildTime: 'unknown',
			gitCommit: 'unknown',
			gitBranch: 'unknown',
		};
	}
}

/**
 * Log version information to console
 * Useful for debugging stale cache issues
 */
export async function logVersionInfo() {
	const info = await fetchVersionInfo();
	console.log('%c🚀 App Version Info', 'font-weight: bold; font-size: 14px; color: #4CAF50;');
	console.log('Version:', info.version);
	console.log('Build Time:', info.buildTime);
	console.log('Git Commit:', info.gitCommit);
	console.log('Git Branch:', info.gitBranch);
	console.log('Current Time:', new Date().toISOString());

	// Calculate how old the build is
	if (info.buildTime !== 'unknown') {
		const buildTime = new Date(info.buildTime);
		const now = new Date();
		const ageMinutes = Math.floor((now - buildTime) / 1000 / 60);
		const ageHours = Math.floor(ageMinutes / 60);
		const ageDays = Math.floor(ageHours / 24);

		if (ageDays > 0) {
			console.log(`Build Age: ${ageDays} days, ${ageHours % 24} hours`);
		} else if (ageHours > 0) {
			console.log(`Build Age: ${ageHours} hours, ${ageMinutes % 60} minutes`);
		} else {
			console.log(`Build Age: ${ageMinutes} minutes`);
		}
	}

	return info;
}

/**
 * Get a formatted version string for display
 * @returns {Promise<string>} Formatted version string
 */
export async function getVersionString() {
	const info = await fetchVersionInfo();
	return `v${info.version} (${info.gitCommit})`;
}

/**
 * Check if the build is stale (older than a certain time)
 * @param {number} maxAgeMinutes - Maximum age in minutes before considering stale
 * @returns {Promise<boolean>} True if build is stale
 */
export async function isBuildStale(maxAgeMinutes = 60 * 24) {
	const info = await fetchVersionInfo();
	if (info.buildTime === 'unknown') {
		return false;
	}

	const buildTime = new Date(info.buildTime);
	const now = new Date();
	const ageMinutes = (now - buildTime) / 1000 / 60;

	return ageMinutes > maxAgeMinutes;
}
